import { type ReactNode, forwardRef } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';

//#region Typings
/**
 * Accepted properties for the PasswordInput tooltip
 * @public
 */
export interface PasswordInputTooltipProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** Elements to be displayed in the PasswordInput tooltip slot, preferably an icon or a similar visual element */
  children?: ReactNode;
  /**
   * If specified, the tooltip is rendered with this text when the password is shown, which describes what the trigger does.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   */
  showText?: string;
  /**
   * If specified, the tooltip is rendered with this text when the password is hidden, which describes what the trigger does.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   */
  hideText?: string;
}

//#endregion

/**
 * Component which allows users to configure the tooltip text and icons of the PasswordInput.
 * @public
 */
export const PasswordInputTooltip = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  PasswordInputTooltipProps
>((props, forwardedRef) => {
  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(props);
  return (
    <div
      ref={forwardedRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
    />
  );
});

(
  PasswordInputTooltip as typeof PasswordInputTooltip & { displayName: string }
).displayName = 'PasswordInput.Tooltip';
